import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useEstimateLineItems } from "@/hooks/useEstimateLineItems";
import { useEstimates } from "@/hooks/useEstimates";
import { PhaseSection } from "@/components/admin/estimates/PhaseSection";
import { MeasurementSummarySection } from "@/components/admin/estimates/MeasurementSummarySection";
import { groupLineItemsByPhase } from "@/utils/proposalMapping";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";
import { WARRANTY_CONTENT } from "@/utils/proposalContent";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import "@/styles/proposal-print.css";
import { Printer, ArrowLeft, CheckCircle, Share2, Phone, Mail, MapPin } from "lucide-react";
import { CustomerProposalGallery } from "@/components/admin/estimates/CustomerProposalGallery";
import { ShareProposalDialog } from "@/components/admin/estimates/ShareProposalDialog";

export default function ProposalView() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [showImages, setShowImages] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  
  const { estimates } = useEstimates();
  const { lineItems } = useEstimateLineItems(estimateId);
  
  const estimate = estimates?.find(e => e.id === estimateId);
  const groupedItems = lineItems ? groupLineItemsByPhase(lineItems) : {};

  const handlePrint = () => {
    window.print();
  };

  if (!estimate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">Loading estimate...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Print Controls (hidden when printing) */}
      <div className="no-print sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Estimate
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-images"
                checked={showImages}
                onCheckedChange={setShowImages}
              />
              <Label htmlFor="show-images" className="cursor-pointer font-medium">
                Include Product Images
              </Label>
            </div>
            
            <Button onClick={() => setShareDialogOpen(true)} variant="outline" size="lg">
              <Share2 className="mr-2 h-4 w-4" />
              Share with Customer
            </Button>
            
            <Button onClick={handlePrint} size="lg">
              <Printer className="mr-2 h-4 w-4" />
              Print / Save as PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Proposal Content */}
      <div className="proposal-container max-w-5xl mx-auto p-8 bg-background">
        {/* Header with Logo */}
        <div className="mb-8 page-break-inside-avoid">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-4">
              <img 
                src="/assets/logos/safeway-roofing-logo.png" 
                alt="Safeway Home Improvements" 
                className="h-16 md:h-20 w-auto"
              />
            </div>
            <div className="text-left md:text-right">
              <p className="text-sm text-muted-foreground">Estimate #</p>
              <p className="text-lg font-bold">{estimate.estimate_number}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(estimate.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Customer Information */}
          <div>
            <h2 className="text-xl font-bold text-foreground mb-4">Proposal For:</h2>
            <div className="space-y-2">
              <p className="text-lg font-semibold">{estimate.customer_name}</p>
              {estimate.customer_phone && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  {estimate.customer_phone}
                </p>
              )}
              {estimate.customer_email && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  {estimate.customer_email}
                </p>
              )}
              {estimate.customer_address && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {estimate.customer_address}, {estimate.city}, {estimate.zip_code}
                </p>
              )}
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Executive Summary */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Project Overview</h2>
          <p className="text-muted-foreground leading-relaxed">
            This comprehensive roofing proposal outlines the complete scope of work, materials, and 
            professional installation process for your project. We use only premium materials and 
            proven installation techniques to ensure lasting protection for your home.
          </p>
        </div>

        {/* Measurement Report */}
        {estimate.measurement_data && (
          <MeasurementSummarySection
            measurementData={estimate.measurement_data}
            measurementSource={estimate.measurement_source}
            fileUrl={estimate.measurement_file_url}
            pdfUrl={estimate.measurement_pdf_url}
          />
        )}

        <Separator className="my-8" />

        {/* Installation Process */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-8">
            Installation Process & Materials
          </h2>
          
          {INSTALLATION_PHASES.map(phase => {
            const phaseItems = groupedItems[phase.key];
            if (!phaseItems || phaseItems.length === 0) return null;
            
            return (
              <PhaseSection
                key={phase.key}
                phase={phase}
                lineItems={phaseItems}
                showImages={showImages}
              />
            );
          })}
        </div>

        <Separator className="my-8" />

        {/* Investment Summary */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Investment Summary</h2>
          <div className="bg-muted/30 rounded-lg p-6 border">
            <div className="flex justify-between items-center text-xl">
              <span className="font-medium text-foreground">Total Investment:</span>
              <span className="font-bold text-primary">
                ${estimate.total?.toLocaleString() || '0'}
              </span>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Warranty Section */}
        <div className="mb-12 page-break-inside-avoid">
          <h2 className="text-2xl font-bold text-foreground mb-6">Warranty Information</h2>
          
          <div className="space-y-8">
            <div className="bg-muted/30 rounded-lg p-6 border">
              <h3 className="text-xl font-semibold text-foreground mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 text-primary mr-2 flex-shrink-0" />
                {WARRANTY_CONTENT.workmanship.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {WARRANTY_CONTENT.workmanship.description}
              </p>
            </div>

            <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
              <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 text-primary mr-2 flex-shrink-0" />
                {WARRANTY_CONTENT.manufacturer.title}
              </h3>
              <ul className="space-y-3">
                {WARRANTY_CONTENT.manufacturer.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-muted/20 rounded-lg border-l-4 border-primary">
            <p className="text-sm text-muted-foreground italic leading-relaxed">
              This warranty gives you peace of mind knowing both Safeway Home Improvements and 
              GAF stand behind your new roof installation.
            </p>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Customer Photos or Project Gallery */}
        <CustomerProposalGallery 
          customerPhotos={estimate.project_photos?.filter((p: any) => p.include_in_proposal !== false)}
          fallbackToGeneric={true}
        />

        {/* Footer */}
        <div className="text-center pt-8 border-t">
          <h3 className="text-xl font-semibold mb-2">Ready to Get Started?</h3>
          <p className="text-muted-foreground mb-4">
            Thank you for considering Safeway Home Improvements for your roofing project.
          </p>
          <p className="text-sm text-muted-foreground">
            Questions? Contact us at <a href="tel:+15551234567" className="text-primary hover:underline">(555) 123-4567</a> or{" "}
            <a href="mailto:info@safewayroofing.com" className="text-primary hover:underline">info@safewayroofing.com</a>
          </p>
        </div>
      </div>

      {/* Share Dialog */}
      <ShareProposalDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        estimateId={estimateId || ""}
        estimateNumber={estimate?.estimate_number || ""}
        customerEmail={estimate?.customer_email}
      />
    </div>
  );
}
